import { type Translated } from '@myre/common-i18n';
import { type HexColor } from '@myre/common-primitives';
import { type TagType } from '@myre/common-tag';
import {
  type DropdownItems,
  type DropdownItemsResolver,
} from '@myre/ui-primitive-actions-dropdown';
import {
  type Element,
  type MultiDateRanges,
} from '@myre/ui-primitive-components';
import { type ElementBase } from '@myre/ui-primitive-forms';
import { type Tooltip } from '@myre/ui-primitive-tooltip-directive';

import { type Signal } from '@angular/core';
import { type UntypedFormGroup } from '@angular/forms';

export enum CellType {
  ActionsDropdown = 'actionsDropdown',
  Button = 'button',
  Color = 'color',
  DateRange = 'dateRange',
  Form = 'form',
  String = 'string',
  Icon = 'icon',
  Tag = 'tag',
  TextWithIconTag = 'textWithIcon',
}

export type Cell =
  | ActionsDropdownCell
  | ButtonCell
  | ColorCell
  | DateRangeCell
  | FormCell
  | IconCell
  | StringCell
  | TagCell
  | TextWithIconTagCell;

interface BaseCell {
  type: CellType;
  tooltip?: Tooltip;
  title?: string;
}

export interface StringCell extends BaseCell {
  type: CellType.String;
  value: string;
  options?: {
    fontWeight?: 'regular' | 'bold';
    textDecoration?: 'underline' | 'line-through';
  };
}

export interface IconCell extends BaseCell {
  type: CellType.Icon;
  icon: string;
  onClick?: (event: MouseEvent) => void;
  options?: {
    color?: HexColor | string;
  };
}

export interface TextWithIconTagCell extends BaseCell {
  type: CellType.TextWithIconTag;
  value: string;
  elements: Element | Element[];
  valueStyles?: {
    fontWeight?: 'regular' | 'bold';
    leftIcon?: boolean;
    /** Elements past the cell width go to the next line instead of overflowing. */
    wrap?: boolean;
  };
}

export interface TagCell extends BaseCell {
  type: CellType.Tag;
  value?: Translated;
  icon?: string;
  options?: {
    type?: TagType;
    bold?: boolean;
  };
}

interface DateRangeCell extends BaseCell {
  type: CellType.DateRange;
  multiDateRanges: MultiDateRanges[];
}

interface ButtonCell extends BaseCell {
  type: CellType.Button;
  value: Translated;
  onClick: (event: MouseEvent | KeyboardEvent) => void;
}

export interface ActionsDropdownCell extends BaseCell {
  type: CellType.ActionsDropdown;
  dropdownItems: DropdownItems | DropdownItemsResolver;
  label?: Translated;
  tagType?: TagType;
  icon?: string;
}

interface FormCell extends BaseCell {
  type: CellType.Form;
  element: Signal<ElementBase | null>;
  form?: (form: UntypedFormGroup) => void;
}

interface ColorCell extends BaseCell {
  type: CellType.Color;
  color: HexColor;
}
